import * as Sentry from '@sentry/vue';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { computed, onMounted, ref, toValue } from 'vue';
import { CanceledError } from 'axios';
import { useActiveUserStore } from '@/stores/user';

import api, { toastMessageFromAxiosError } from '@/helpers/api';
import type { UploadProgressEvent } from '@/api/orderFiles';
import {
  useModal,
  useToast,
  useTrack,
  useTranslation,
} from '@/composables/plugins';
import {
  type CadFile,
  type Order,
  type User,
  PRODUCTION_ASSET_TYPES,
  QUOTING_ASSET_TYPES,
} from '@cutr/constants/order';
import { useFilesManagement } from '@/composables/useFilesManagement';
import { useOrderStore } from '@/stores/order';
import { useFileUploadEffects } from '@/components/pages/Order/composables/useFileUploadEffects';
import { useOrderFiles } from '@/stores/orderFiles';
import { useMaterialRequestStore } from '@/stores/orderMaterialRequest';
import { usePartsStore } from '@/stores/parts';

import { getOrderUserType } from '@/helpers/user-helpers';
import { storeToRefs } from 'pinia';

export const useRevisions = () => {
  const { currentOrder } = storeToRefs(useOrderStore());
  const toast = useToast();
  const userStore = useActiveUserStore();
  const { t } = useTranslation();

  const loading = ref(false);
  const revisions = ref([] as CadFile[]);

  const pendingRevision = computed(() => {
    const [latest] = revisions.value;
    return latest?.revisionReview?.status === 'pending-review' ? latest : null;
  });
  const revisionHistory = computed(() => {
    const [, ...tail] = revisions.value;

    return (pendingRevision.value ? tail : revisions.value) as CadFile[];
  });

  function getUserName(user: User) {
    if (user.id === userStore.activeUserId) return 'you';

    if (
      getOrderUserType(currentOrder.value, user) === 'Maker' &&
      userStore.isCustomer
    ) {
      return t('order-files:formUpload.approval.partner');
    }
    if (
      getOrderUserType(currentOrder.value, user) === 'Customer' &&
      userStore.isMaker
    ) {
      return t('order-files:formUpload.approval.customer');
    }

    if (user.name && user.companyName)
      return `${user.name} (${user.companyName})`;
    if (user.name) return user.name;

    return t('order-files:history.unknownUser');
  }

  let controller: AbortController | null = null;
  function fetchRevisions(id: string) {
    if (controller) {
      controller.abort();
    }
    controller = new AbortController();
    loading.value = true;
    api
      .get(`/v2/order-files/${id}/revisions`, { signal: controller.signal })
      .then(({ data }) => {
        revisions.value = data.data;
      })
      .catch((err) => {
        if (err instanceof CanceledError) return;
        console.error(err);
        toast.error(`Error fetching revisions: ${err}`);
      })
      .finally(() => {
        loading.value = false;
      });
  }

  return {
    loading,
    fetchRevisions,
    revisions,
    pendingRevision,
    revisionHistory,
    getUserName,
  };
};

export const useReviewApproval = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const responseLoading = ref(false);

  async function onStatusChange(status: string, id: string) {
    responseLoading.value = true;
    return api
      .post(`/v2/revision-reviews/${id}`, {
        status,
      })
      .then(() => {
        toast(
          t('order-files:review.successMessage', {
            statusLabel: t(`order-files:review.status.${status}`).toLowerCase(),
          })
        );
      })
      .catch((error) => {
        const message = error?.response?.data?.message;

        toast.error(message || 'There was an error submitting the response.');
      })
      .finally(() => {
        responseLoading.value = false;
      });
  }

  async function onRevoke(id: string) {
    responseLoading.value = true;
    return api
      .delete(`/v2/revision-reviews/${id}`)
      .then(() => {
        toast(`Revision revoked`);
      })
      .catch((error) => {
        const message = error?.response?.data?.message;

        toast.error(message || 'There was an error submitting the response.');
      })
      .finally(() => {
        responseLoading.value = false;
      });
  }

  return { responseLoading, onStatusChange, onRevoke };
};

export type UploadRevisionArgs = {
  originalFile?: CadFile;
  newFile: File;
  requestReview?: boolean;
  requestCustomerReview?: boolean;
  requestMakerReview?: boolean;
  progress?: (progress: number) => void;
  assetType: CadFile['assetType'];
  productionFileType?: string;
  folderId?: string;
  // Position to place a NEW file in directly ('inbox' => order-level, no folder).
  // Ignored for revisions.
  positionId?: string;
  visibleToCustomer?: boolean;
  visibleToProductionPartner?: boolean;
  // When false, skip selecting the uploaded file as the active file.
  // The caller commits to refreshing the file list itself. Defaults to true.
  selectUploadedFile?: boolean;
};

export function useOrderFileUpload(variantId: Ref<string>) {
  const { currentOrder } = storeToRefs(useOrderStore());
  const fileStore = useOrderFiles();
  const { onFileUploaded } = useFileUploadEffects();
  const track = useTrack();
  const toast = useToast();

  return async function uploadFile(opts: UploadRevisionArgs) {
    const formData = new FormData();
    formData.append('orderVariantId', variantId.value);
    if (opts.productionFileType) {
      formData.append('productionFileType', opts.productionFileType);
    }
    formData.append('assetType', opts.assetType);
    formData.append(
      'requestCustomerReview',
      opts.requestCustomerReview ? 'true' : 'false'
    );
    formData.append(
      'requestMakerReview',
      opts.requestMakerReview ? 'true' : 'false'
    );
    formData.append('orderFile', opts.newFile);

    if (opts.folderId) {
      formData.append('folderId', opts.folderId);
    }

    if (opts.positionId) {
      formData.append('positionId', opts.positionId);
    }

    if (opts.visibleToCustomer !== undefined) {
      formData.append(
        'visibleToCustomer',
        opts.visibleToCustomer ? 'true' : 'false'
      );
    }

    if (opts.visibleToProductionPartner !== undefined) {
      formData.append(
        'visibleToProductionPartner',
        opts.visibleToProductionPartner ? 'true' : 'false'
      );
    }

    const onUploadProgress = (xhr: UploadProgressEvent) => {
      // Without a total there is no percentage to report.
      if (!xhr.total) return;
      opts.progress?.((xhr.loaded / xhr.total) * 100);
    };

    try {
      const newOrderFile = opts.originalFile
        ? await fileStore.reviseFile({
            originalFileId: opts.originalFile.id,
            formData,
            onUploadProgress,
          })
        : await fileStore.uploadFile({ formData, onUploadProgress });

      if (opts.selectUploadedFile !== false) {
        await onFileUploaded(newOrderFile);
      }
      return newOrderFile;
    } catch (err) {
      track('order:error', {
        event: 'cadfile-upload',
        err,
        order: currentOrder.value,
      });
      Sentry.captureException(err);
      toast.error(
        `Error uploading ${opts.newFile.name}: ${toastMessageFromAxiosError(
          err
        )}`
      );
      throw err;
    }
  };
}
export function useFileItem(
  fileSource: MaybeRefOrGetter<CadFile>,
  readonly = false
) {
  const file = computed(() => toValue(fileSource));
  const toast = useToast();
  const userStore = useActiveUserStore();
  const parts = usePartsStore();
  const { currentOrder } = storeToRefs(useOrderStore());

  const analysisNotRun = computed(
    () => file.value.type == '3D' && file.value.analysisStatus == 'not_run'
  );

  const hasPendingReview = computed(
    () => file.value.revisionReview?.status === 'pending-review'
  );

  const partsNum = computed(
    () =>
      parts.partsForFile[file.value.id]?.filter(
        (p) => p.source !== 'placeholder'
      ).length ||
      file.value.totalParts ||
      0
  );

  const canViewRevisions = computed(
    () => userStore.activeSessionType !== 'Maker' && !file.value.archivedAt
  );
  const isCutlist = computed(() => file.value.type === 'cutlist');
  const isCabinet = computed(() => file.value.type === 'cabinet');
  const isDesignFile = computed(() =>
    QUOTING_ASSET_TYPES.includes(file.value.assetType)
  );

  const canUploadRevision = computed(
    () =>
      !isCutlist.value &&
      !isCabinet.value &&
      (userStore.isAdmin ||
        !PRODUCTION_ASSET_TYPES.includes(file.value.assetType)) // only admins can upload revisions for production files
  );

  onMounted(() => {
    const oneDayInPast = new Date().getTime() - 1 * 24 * 60 * 60 * 1000;
    const created = new Date(file.value.createdAt).getTime();

    if (analysisNotRun.value && created < oneDayInPast) {
      triggerFileAnalysis(true);
    }
  });

  function triggerFileAnalysis(mute = false) {
    return api
      .post(`/v2/order-files/${file.value.id}/analyse`)
      .then(() => {
        !mute && toast('Analysis scheduled');
      })
      .catch((err) => {
        const message = err.response?.data?.message || err;
        !mute && toast.error(`Failed to retrigger analysis: ${message}`);
      });
  }

  return {
    isCutlist,
    isDesignFile,
    analysisNotRun,
    hasPendingReview,
    canViewRevisions,
    canUploadRevision,
    triggerFileAnalysis,
    status: getReviewStatus(
      file.value,
      userStore.activeUser,
      currentOrder.value
    ),
    partsNum,
    isCabinet,
  };
}

// These are all file types that are related to CAD files part of a project
// containing multiple files that are referencing each other by file name.
// Renaming these files would break the references and thus break the project.
export const NO_RENAME_ALLOWED = [
  'sldprt',
  'sldasm',
  'ipt',
  'iam',
  'rfa',
  'rft',
  'rte',
  'rvt',
];

export function useRemoveFile({
  onDone,
  onCancel,
}: {
  onDone(): void;
  onCancel(): void;
}) {
  const toast = useToast();
  const modal = useModal();
  const requestStore = useMaterialRequestStore();
  const { i18next } = useTranslation();
  const modalT = i18next.getFixedT(null, 'order', 'modals.delete-file');
  const { isPositionsOrder } = useFilesManagement();
  function onFileRemoved(file: CadFile) {
    modal.open('confirm', {
      header: modalT('header'),
      body: modalT('body'),
      confirmIcon: 'delete',
      cancelIcon: null,
      confirmColor: 'alert',
      confirmText: modalT('confirmButton'),
      onConfirm: () => removeFile(file),
      ...(isPositionsOrder.value
        ? {}
        : {
            cancelText: modalT('cancelButton'),
            onCancel: () =>
              modal.open('upload-order-file-with-revision', {
                originalFile: file,
                assetType: file.assetType,
              }),
          }),
      onClose: onCancel,
    });
  }

  async function removeFile(file: CadFile) {
    return api
      .delete(`/v2/order-files/${file.id}`)
      .then(() => {
        toast('File removed');
        return requestStore.invalidateMaterialRequests();
      })
      .catch(() => {
        toast.error(`Error removing cad file`);
      })
      .finally(onDone);
  }

  return { onFileRemoved, removeFile };
}

export function getReviewStatus(
  file: CadFile,
  user: Partial<User>,
  order: Partial<Order>
) {
  if (!file.user) return;

  const reviews = file.revisionReviews;

  if (!reviews?.length) return null;

  if (reviews.every((review) => review.status === 'approved')) {
    return 'approved';
  }

  if (reviews.some((review) => review.status === 'rejected')) {
    return 'rejected';
  }

  const userType =
    // eslint-disable-next-line no-restricted-properties
    user.type === 'User' ? getOrderUserType(order, user) : user.type;

  const activeUserReview = file.revisionReviews?.find(
    (review) => review.assigneeType === userType
  );

  if (activeUserReview?.status === 'pending-review') return 'needs-review';

  if (reviews.some((review) => review.status === 'pending-review')) {
    return 'pending-review';
  }

  return null;
}

export function canUserDeleteFile({
  file,
  sessionType,
  order,
}: {
  file: CadFile;
  sessionType: string;
  order: Order;
}) {
  if (sessionType === 'Admin' && !file.archivedAt) return true;
  // you cannot delete files that do not belong to you
  // here we assume orders have only one customer and one maker
  if (getOrderUserType(order, file.user) !== sessionType) return false;
  // you can delete your files while in draft
  if (order.orderstatus === 'draft') return true;

  if (sessionType === 'Customer') {
    if (file.revisionReviews?.length === 0) return true;
    return Boolean(
      !file.revisionReviews?.find(
        (rev) => rev.assigneeType === 'Admin' && rev.status !== 'pending-review'
      )
    );
  }

  if (sessionType === 'Maker') {
    return !file.hasViews;
  }

  return false;
}
